import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ChevronsLeft,
  ChevronsRight,
  LayoutDashboard,
  LogOut,
  Map as MapIcon,
  MapPin,
  Route as RouteIcon,
  ShieldCheck,
  Sparkles
} from 'lucide-react';
import Badge from '../ui/Badge';
import Tooltip from '../ui/Tooltip';
import { cn } from '../../lib/cn';
import { useAuth } from '../../hooks/useAuth';

const NAV = [
  { key: 'data',   label: 'Dashboard', icon: LayoutDashboard, indicator: true },
  { key: 'stops',  label: 'Stops',     icon: MapPin },
  { key: 'map',    label: 'Map',       icon: MapIcon },
  { key: 'routes', label: 'Routes',    icon: RouteIcon, editorOnly: true }
];

export default function Sidebar({ controller }) {
  const {
    activeTab,
    savedRoutes,
    sidebarCollapsed,
    stops,
    switchTab,
    toggleSidebar,
    visibleStopIds,
    viewingRoute,
    viewingRouteIds
  } = controller;

  const { user, logout, isEditor, isSuperAdmin, roleLabel } = useAuth();
  const navigate = useNavigate();
  const collapsed = sidebarCollapsed;
  const username = user?.username || 'user';
  const displayName = user?.name || username;
  const initials = displayName.slice(0, 2).toUpperCase();
  const totalSelected =
    visibleStopIds.size +
    (viewingRoute ? 1 : 0) +
    (viewingRouteIds ? viewingRouteIds.size : 0);

  return (
    <aside
      className={cn(
        'shrink-0 h-screen sticky top-0 bg-white border-r border-slate-200 flex flex-col transition-[width] duration-200 ease-out',
        collapsed ? 'w-[68px]' : 'w-[240px]'
      )}
    >
      <div className={cn('flex items-center gap-2.5 px-3 pt-4 pb-3', collapsed && 'justify-center px-2')}>
        <div className="h-9 w-9 rounded-lg bg-gradient-to-br from-brand-600 to-brand-500 shadow-soft flex items-center justify-center text-white shrink-0">
          <Sparkles className="h-4 w-4" />
        </div>
        {!collapsed && (
          <div className="leading-tight min-w-0">
            <p className="text-[14px] font-semibold text-slate-900 tracking-tight truncate">Route Manager</p>
            <p className="text-[11px] text-slate-500 truncate">Thane · Operations workspace</p>
          </div>
        )}
      </div>

      <div className={cn('px-3', collapsed && 'px-2')}>
        {!collapsed && (
          <p className="text-[10px] font-semibold uppercase tracking-[0.14em] text-slate-400 px-2 mt-2 mb-1.5">
            Workspace
          </p>
        )}
        <nav className="flex flex-col gap-0.5">
          {NAV.filter((item) => !item.editorOnly || isEditor).map((item) => {
            const Icon = item.icon;
            const active = activeTab === item.key;
            const button = (
              <button
                key={item.key}
                type="button"
                onClick={() => switchTab(item.key)}
                className={cn(
                  'group flex items-center gap-2.5 h-9 rounded-md text-[13px] font-medium transition-all duration-150',
                  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 focus-visible:ring-offset-2',
                  collapsed ? 'justify-center px-0 w-full' : 'px-2.5 w-full',
                  active
                    ? 'bg-brand-50 text-brand-700 shadow-soft'
                    : 'text-slate-600 hover:bg-slate-100 hover:text-slate-900'
                )}
              >
                <Icon className={cn('h-4 w-4 shrink-0', active ? 'text-brand-600' : 'text-slate-500 group-hover:text-slate-700')} />
                {!collapsed && (
                  <>
                    <span className="flex-1 text-left truncate">{item.label}</span>
                    {active && <span className="h-1.5 w-1.5 rounded-full bg-brand-500 shrink-0" />}
                  </>
                )}
              </button>
            );

            if (collapsed) {
              return (
                <Tooltip key={item.key} label={item.label} side="right">
                  {button}
                </Tooltip>
              );
            }
            return button;
          })}
        </nav>
      </div>

      {isSuperAdmin && (
        <div className={cn('px-3 mt-4', collapsed && 'px-2')}>
          {!collapsed && (
            <p className="text-[10px] font-semibold uppercase tracking-[0.14em] text-slate-400 px-2 mb-1.5">
              Governance
            </p>
          )}
          {(() => {
            const button = (
              <button
                type="button"
                onClick={() => navigate('/command-center')}
                className={cn(
                  'group flex items-center gap-2.5 h-9 rounded-md text-[13px] font-medium transition-all duration-150',
                  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 focus-visible:ring-offset-2',
                  'text-slate-600 hover:bg-emerald-50 hover:text-emerald-700',
                  collapsed ? 'justify-center px-0 w-full' : 'px-2.5 w-full'
                )}
              >
                <ShieldCheck className="h-4 w-4 shrink-0 text-emerald-500 group-hover:text-emerald-600" />
                {!collapsed && <span className="flex-1 text-left truncate">Command Center</span>}
              </button>
            );
            return collapsed ? (
              <Tooltip label="Command Center" side="right">
                {button}
              </Tooltip>
            ) : (
              button
            );
          })()}
        </div>
      )}

      {!collapsed && (
        <div className="px-3 mt-4">
          <div className="rounded-lg border border-brand-100 bg-gradient-to-br from-brand-50 via-white to-white p-3">
            <p className="text-[10px] font-semibold uppercase tracking-[0.14em] text-brand-600 mb-2">
              At a glance
            </p>
            <Stat label="Stops" value={stops.length} />
            <Stat label="Routes" value={savedRoutes.length} />
            {totalSelected > 0 && (
              <div className="mt-2 pt-2 border-t border-brand-100 flex items-center justify-between text-[11.5px]">
                <span className="text-slate-500">Selected now</span>
                <span className="font-semibold text-brand-700 tabular-nums">{totalSelected}</span>
              </div>
            )}
          </div>
        </div>
      )}

      <div className="flex-1" />

      <div className={cn('px-3 py-3 border-t border-slate-100', collapsed && 'px-2')}>
        <div className={cn('flex items-center', collapsed ? 'justify-center' : 'gap-2.5')}>
          <span className="h-8 w-8 rounded-full bg-brand-100 text-brand-700 inline-flex items-center justify-center text-[11px] font-semibold ring-2 ring-white shadow-soft shrink-0">
            {initials}
          </span>
          {!collapsed && (
            <>
              <div className="flex-1 min-w-0">
                <p className="text-[12.5px] font-semibold text-slate-900 truncate">{displayName}</p>
                <Badge
                  variant={isSuperAdmin ? 'success' : isEditor ? 'brand' : 'default'}
                  size="sm"
                  dot
                  className="mt-0.5"
                >
                  {roleLabel || (isEditor ? 'Admin' : 'Viewer')}
                </Badge>
              </div>
              <Tooltip label="Sign out">
                <button
                  type="button"
                  onClick={logout}
                  className="h-7 w-7 inline-flex items-center justify-center rounded-md text-slate-400 hover:bg-rose-50 hover:text-rose-600 transition-colors"
                >
                  <LogOut className="h-3.5 w-3.5" />
                </button>
              </Tooltip>
            </>
          )}
        </div>

        {collapsed && (
          <Tooltip label="Sign out" side="right">
            <button
              type="button"
              onClick={logout}
              className="mt-2 w-full h-7 inline-flex items-center justify-center rounded-md text-slate-400 hover:bg-rose-50 hover:text-rose-600 transition-colors"
            >
              <LogOut className="h-3.5 w-3.5" />
            </button>
          </Tooltip>
        )}

        <button
          type="button"
          onClick={toggleSidebar}
          className={cn(
            'mt-2 w-full h-7 inline-flex items-center justify-center gap-1.5 rounded-md text-[11.5px] font-medium text-slate-500 hover:bg-slate-100 hover:text-slate-700 transition-colors',
            'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 focus-visible:ring-offset-2'
          )}
        >
          {collapsed ? <ChevronsRight className="h-3.5 w-3.5" /> : (
            <>
              <ChevronsLeft className="h-3.5 w-3.5" />
              <span>Collapse</span>
            </>
          )}
        </button>
      </div>
    </aside>
  );
}

function Stat({ label, value }) {
  return (
    <div className="flex items-center justify-between py-0.5">
      <span className="text-[12px] text-slate-600">{label}</span>
      <span className="text-[12.5px] font-semibold text-slate-900 tabular-nums">{value}</span>
    </div>
  );
}
